import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import * as OrganizationActions from 'actions/OrganizationActions';
import BenchlingConfig from 'BenchlingConfig';
import {Institution} from 'institutions/InstitutionConstants';
import InstitutionSelector from 'institutions/InstitutionSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {Header1} from 'ui-library/Headers';
import * as nyc from 'ui-library/NYC';
import {InputChangeEvent} from 'util/Types';

import InviteMembersSection from './InviteMembersSection';
import {Member} from './OrganizationConstants';

export interface CreateOrganizationPageProps {
  isLoading: boolean;
  organization: {name: string};
}

interface OrganizationState {
  name: string;
  handle: string;
  institution?: Institution;
}

interface State {
  avatar: File | null;
  members: Array<Member>;
  organization: OrganizationState;
}

class CreateOrganizationPage extends React.Component<CreateOrganizationPageProps, State> {
  constructor(props: CreateOrganizationPageProps) {
    super(props);
    const organization = {
      name: props.organization.name,
      handle: '',
    };

    this.state = {
      avatar: null,
      members: [],
      organization,
    };
  }

  _updateCurrentOrganization = (organizationAttribute: Partial<OrganizationState>): void => {
    this.setState({
      organization: reactUpdate(this.state.organization, {$merge: organizationAttribute}),
    });
  };

  _handleCreateOrganization = (): void => {
    // Extract any users and emails to invite to the organization upon creation.
    const invites = _.groupBy(this.state.members, (member) => (member.handle ? 'users' : 'emails'));
    const usersToInvite = invites.users ? invites.users.map((member) => member.id) : [];
    const emailsToInvite = invites.emails
      ? invites.emails
          .map((member) => member.email)
          .filter((maybeEmail): maybeEmail is string => maybeEmail != null)
      : [];

    const institutionId = this.state.organization.institution ? this.state.organization.institution.id : null;
    OrganizationActions.createWithInfo(
      {
        name: this.state.organization.name,
        handle: this.state.organization.handle,
        avatar: this.state.avatar,
        institution: institutionId,
      },
      usersToInvite,
      emailsToInvite
    );
  };

  _handleInviteMember = (member: Member): void => {
    this.setState({members: reactUpdate(this.state.members, {$push: [member]})});
  };

  _handleRemoveInvite = (i: number): void => {
    this.setState({members: reactUpdate(this.state.members, {$splice: [[i, 1]]})});
  };

  _updateInstitution = (institution: Institution): void => {
    this._updateCurrentOrganization({institution});
  };

  override render(): JSX.Element {
    const enableCreateOrganization = Boolean(this.state.organization.name && this.state.organization.handle);
    return (
      <nyc.Container size="lg">
        <nyc.Row>
          <nyc.Col lg={1} />
          <nyc.Col lg={21}>
            <div className="createOrganizationPage-title">Set up your organization on Benchling</div>
          </nyc.Col>
        </nyc.Row>
        <nyc.Row>
          <nyc.Col lg={1} />
          <nyc.Col lg={9}>
            <div className="u-spacing-bottom u-vertical-center-items">
              <div className="featureBanner-stepNumber">1</div>
              <Header1>Tell us a little about your organization</Header1>
            </div>
            <form onSubmit={this._handleCreateOrganization}>
              <FormGroup className="u-spacing-top">
                <Label>Give your organization a name</Label>
                <FormInput
                  value={this.state.organization.name}
                  placeholder="e.g. Haynes Lab (required)"
                  onChange={(e: InputChangeEvent) => {
                    this._updateCurrentOrganization({name: e.target.value});
                  }}
                  disabled={this.props.isLoading}
                />
              </FormGroup>
              <FormGroup>
                <Label>Give your organization a handle (no spaces)</Label>
                <FormInput
                  name="__createOrgHandle"
                  placeholder="e.g. hlab (required)"
                  value={this.state.organization.handle}
                  onChange={(e: InputChangeEvent) => {
                    this._updateCurrentOrganization({handle: e.target.value});
                  }}
                  disabled={this.props.isLoading}
                />
              </FormGroup>
              {!BenchlingConfig.PRIVATE_DEPLOY && (
                <FormGroup>
                  <Label>Add your institute or university name</Label>
                  <InstitutionSelector
                    allowUnverified={false}
                    onChange={this._updateInstitution}
                    placeholder="Select an institution or university"
                    value={this.state.organization.institution}
                  />
                </FormGroup>
              )}
              <Label>Add an avatar to represent your organization</Label>
              <input
                type="file"
                className="u-spacing-bottom"
                disabled={this.props.isLoading}
                onChange={(e) => {
                  this.setState({avatar: e.target.files![0]});
                }}
              />
            </form>
          </nyc.Col>
          <nyc.Col lg={1} />
          <nyc.Col lg={9}>
            <InviteMembersSection
              members={this.state.members}
              onInviteMember={this._handleInviteMember}
              onRemoveInvite={this._handleRemoveInvite}
            />
            <div className="u-justify-content-end u-spacing-top">
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                disabled={this.props.isLoading || !enableCreateOrganization}
                onClick={this._handleCreateOrganization}
                size={ButtonSize.MEDIUM}
                text={this.props.isLoading ? 'Creating...' : 'Next: Share Projects'}
                dataTestAttrs={{component: 'CreateOrganizationPage', element: 'nextShareProjects'}}
              />
            </div>
          </nyc.Col>
        </nyc.Row>
      </nyc.Container>
    );
  }
}
export default CreateOrganizationPage;
